import * as React from "react";
import { Check, Copy } from "lucide-react";

import { useCopyFeedback } from "../lib/copy";
import { cn } from "../lib/utils";
import { IconButton } from "./icon-button";

/**
 * v2 InfoList + InfoRow (redesign phase 5, #455): label/value pairs, e.g. a
 * contact's or a machine's facts (breaks #124-#125). Same file name as v1
 * `components/ui/info-row.tsx`, and InfoRow keeps v1's `label` / `value`
 * props, so the swap is a move. Storybook only until implementation; see
 * docs/design-library/04-implementation-notes.md.
 *
 * Semantics: `InfoList` is a `<dl>`, every `InfoRow` a `<div>` holding a
 * `<dt>` and a `<dd>`.
 *
 * Layout is chosen per list, never per row:
 *   stacked  label above value, 2 apart; rows 12 apart, columns 24 (`columns`)
 *   inline   label column 160 left, value right, 16 apart; rows 8 apart
 *
 * Label: Sora 12/16 Semibold `--ink-muted`, sentence case: the library's one
 * small-label style (table header, menu group label, nav section heading).
 * Not the uppercase eyebrow (break #125): German loses its noun capitals in
 * caps, ß turns into SS ("MASSE": Maße or Masse?) and long compounds get wider.
 * Labels wrap, never truncate.
 *
 * Value: Inter 14/20 `--ink`, wraps (long serials and URLs too). Any node:
 * Chip / ChipGroup, Badge, a link, Avatar + name. Empty (`null`, `undefined`,
 * `""`) shows "—" with a hidden "nicht angegeben"; `0` is a value.
 *
 * Copy: `copyValue` adds an `xs` ghost IconButton ("E-Mail kopieren"). After a
 * click its label (and tooltip) reads "Kopiert" for 2 s, or "Kopieren
 * fehlgeschlagen" when the clipboard rejects or is missing (add-in iframe,
 * insecure context). One polite status per list says it too. No toast.
 *
 * No dividers: spacing and type carry the structure.
 */

export type InfoListLayout = "stacked" | "inline";
export type InfoListColumns = 1 | 2 | 3 | 4;

/** German defaults; replace any of them. */
export interface InfoListLabels {
  /** Hidden text for an empty value. */
  empty: string;
  /** The copy button's name. */
  copy: (label: string) => string;
  /** The button's name after a copy. */
  copied: string;
  /** The status after a copy. */
  copiedStatus: (label: string) => string;
  /** Button name and status after a failed copy. */
  copyFailed: string;
}

export const DEFAULT_INFO_LIST_LABELS: InfoListLabels = {
  empty: "nicht angegeben",
  copy: (label) => `${label} kopieren`,
  copied: "Kopiert",
  copiedStatus: (label) => `${label} kopiert`,
  copyFailed: "Kopieren fehlgeschlagen",
};

const InfoListContext = React.createContext<{
  layout: InfoListLayout;
  labels: InfoListLabels;
  announce: (message: string) => void;
}>({ layout: "stacked", labels: DEFAULT_INFO_LIST_LABELS, announce: () => {} });

const COLUMNS: Record<InfoListColumns, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
};

export interface InfoListProps extends React.HTMLAttributes<HTMLDListElement> {
  layout?: InfoListLayout;
  /** Stacked only. Fewer columns below the `sm` / `lg` breakpoints. */
  columns?: InfoListColumns;
  labels?: Partial<InfoListLabels>;
}

const InfoList = React.forwardRef<HTMLDListElement, InfoListProps>(function InfoList(
  { layout = "stacked", columns = 1, labels, className, children, ...props },
  ref,
) {
  const [message, setMessage] = React.useState("");
  const context = { layout, labels: { ...DEFAULT_INFO_LIST_LABELS, ...labels }, announce: setMessage };

  return (
    <>
      <dl
        ref={ref}
        data-layout={layout}
        className={cn(
          layout === "stacked" ? cn("grid gap-x-6 gap-y-3", COLUMNS[columns]) : "flex flex-col gap-2",
          className,
        )}
        {...props}
      >
        <InfoListContext.Provider value={context}>{children}</InfoListContext.Provider>
      </dl>
      {/* One status per list, always mounted so the change is announced. Outside the <dl>: only rows go in there. */}
      <span role="status" className="sr-only" data-slot="info-list-status">
        {message}
      </span>
    </>
  );
});

export interface InfoRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "onCopy"> {
  label: string;
  value?: React.ReactNode;
  /** Adds the copy button; the text that goes to the clipboard. */
  copyValue?: string;
  /** Replaces the clipboard write; a throw or rejection shows the failure. */
  onCopy?: (value: string) => void | Promise<void>;
}

const InfoRow = React.forwardRef<HTMLDivElement, InfoRowProps>(function InfoRow(
  { label, value, copyValue, onCopy, className, ...props },
  ref,
) {
  const { layout, labels, announce } = React.useContext(InfoListContext);
  const inline = layout === "inline";
  const empty = value == null || value === "" || value === false;

  const { state: copyState, copy: copyText } = useCopyFeedback(onCopy);
  const copy = async (text: string) => {
    const ok = await copyText(text);
    announce(ok ? labels.copiedStatus(label) : labels.copyFailed);
  };

  const copyLabel = copyState === "copied" ? labels.copied : copyState === "failed" ? labels.copyFailed : labels.copy(label);

  return (
    <div
      ref={ref}
      data-slot="info-row"
      className={cn(inline ? "flex items-baseline gap-4" : "flex min-w-0 flex-col gap-0.5", className)}
      {...props}
    >
      <dt className={cn("font-sans text-caption font-semibold break-words text-ink-muted", inline && "w-40 shrink-0")}>
        {label}
      </dt>
      <dd className="flex min-w-0 flex-1 items-start gap-1 font-body text-body wrap-break-word text-ink">
        <div className="min-w-0">
          {empty ? (
            <>
              <span aria-hidden="true" className="text-ink-muted">
                —
              </span>
              <span className="sr-only">{labels.empty}</span>
            </>
          ) : (
            value
          )}
        </div>
        {copyValue != null && !empty && (
          // -my-0.5: the 24px button sits on the 20px text line without growing it.
          <IconButton
            size="xs"
            className="-my-0.5"
            icon={copyState === "copied" ? Check : Copy}
            label={copyLabel}
            onClick={() => void copy(copyValue)}
          />
        )}
      </dd>
    </div>
  );
});

export { InfoList, InfoRow };
